import { useState } from "react";
import { useLocation, useNavigate, Outlet } from "react-router-dom";
import type { RootState } from "@/store";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { MobileBottomNav } from "@/components/mobile";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard,
  PlusCircle,
  List,
  MessageSquare,
  Star,
  BarChart3,
  Wallet,
  Settings,
  Bell,
  LogOut,
  User,
  ChevronDown,
  Store,
  TrendingUp,
  CheckCircle2,
  Shield,
  FileText,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
} from "lucide-react";
import { useProfile } from "@/hooks";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { logOut } from "@/store/actions/authActions";
import ScrollToTop from "../ScrollToTop";
import SmoothScroll from "../SmoothScroll";

// Vendor navigation items
const vendorNavItems = [
  {
    title: "Dashboard",
    path: "/vendor",
    icon: LayoutDashboard,
  },
  { title: "My Listings", path: "/vendor/listings", icon: List },
  {
    title: "Create Listing",
    path: "/vendor/listing/new",
    icon: PlusCircle,
  },
  {
    title: "Messages",
    path: "/vendor/messages",
    icon: MessageSquare,
    badge: "3",
  },
  { title: "Reviews", path: "/vendor/reviews", icon: Star },
  { title: "Analytics", path: "/vendor/analytics", icon: BarChart3 },
  { title: "Wallet", path: "/vendor/wallet", icon: Wallet },
  { title: "Documents", path: "/vendor/documents", icon: FileText },
  { title: "Settings", path: "/vendor/settings", icon: Settings },
];

export default function VendorLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);

  const userId = user?.id || "";

  const { data: profile, isLoading, isError } = useProfile(userId);

  const [isCollapsed, setIsCollapsed] = useState(false);

  const handleLogout = () => {
    dispatch(logOut());
    toast({
      title: "Logged out",
      description: "You have been successfully logged out.",
    });
    navigate("/login");
  };

  const isActive = (path: string) => {
    return (
      location.pathname === path || location.pathname.startsWith(path + "/")
    );
  };

  // Check if we're on a sub-page (not the main vendor dashboard)
  const isSubPage = location.pathname !== "/forex/vendor/dashboard";

  // Get page title based on current route
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === "/vendor") return "Vendor Dashboard";
    if (path.includes("/listings")) return "My Listings";
    if (path.includes("/listing/new")) return "Create Listing";
    if (path.includes("/messages")) return "Messages";
    if (path.includes("/reviews")) return "Reviews";
    if (path.includes("/analytics")) return "Analytics";
    if (path.includes("/wallet")) return "Wallet";
    if (path.includes("/documents")) return "Documents";
    if (path.includes("/banks")) return "Bank Accounts";
    if (path.includes("/settings")) return "Settings";
    return "Vendor";
  };

  return (
    <TooltipProvider delayDuration={100}>
      <div className="flex min-h-screen w-full bg-background">
        <ScrollToTop />
        <SmoothScroll />
        {/* Desktop Sidebar - Hidden on mobile */}
        <aside
          className={cn(
            "fixed left-0 top-0 h-screen z-40 bg-sidebar border-r border-sidebar-border flex-col transition-all duration-300 ease-in-out hidden lg:flex",
            isCollapsed ? "w-[70px]" : "w-[260px]",
          )}
        >
          {/* Sidebar Header */}
          <div className="h-16 border-b border-sidebar-border flex items-center px-3 shrink-0">
            <div
              className="flex items-center gap-3 cursor-pointer"
              onClick={() => navigate("/vendor/dashboard")}
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 shrink-0">
                <Store className="h-5 w-5 text-white" />
              </div>
              {!isCollapsed && (
                <div className="flex flex-col">
                  <span className="text-sm font-bold text-sidebar-foreground">
                    Vendor
                  </span>
                  <span className="text-xs text-sidebar-foreground/60">
                    Forex Exchange
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar Content - Scrollable */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden py-2">
            {/* Stats Card (when expanded) */}
            {!isCollapsed && (
              <div className="px-3 py-3">
                <div
                  className="rounded-xl bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border border-emerald-500/20 p-3 cursor-pointer hover:border-emerald-500/40 transition-colors"
                  onClick={() => navigate("/vendor/wallet")}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="h-7 w-7 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                      <TrendingUp className="h-4 w-4 text-emerald-500" />
                    </div>
                    <div>
                      <p className="text-xs text-sidebar-foreground/60">
                        Today's Earnings
                      </p>
                      <p className="text-base font-bold text-emerald-500">
                        ₦245,000
                      </p>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-sidebar-foreground/60">
                        Weekly Goal
                      </span>
                      <span className="text-emerald-500">78%</span>
                    </div>
                    <Progress value={78} className="h-1.5 bg-sidebar-accent" />
                  </div>
                </div>
              </div>
            )}

            {/* Sidebar Navigation */}
            <nav className="px-2">
              <ul className="space-y-1">
                {vendorNavItems.map((item) => {
                  const active = isActive(item.path);

                  return (
                    <li key={item.path}>
                      {isCollapsed ? (
                        <Tooltip delayDuration={100}>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => navigate(item.path)}
                              className={cn(
                                "w-full flex items-center justify-center px-3 py-2.5 rounded-lg transition-all duration-200 relative",
                                active
                                  ? "bg-emerald-500/20 text-emerald-500"
                                  : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground",
                              )}
                            >
                              <item.icon
                                className={cn(
                                  "h-5 w-5",
                                  active ? "text-emerald-500" : "",
                                )}
                              />
                              {active && (
                                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-emerald-500 rounded-r-full" />
                              )}
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="right" sideOffset={10}>
                            <div className="flex items-center gap-2">
                              {item.title}
                              {item.badge && (
                                <Badge className="h-4 min-w-4 px-1 text-[10px]">
                                  {item.badge}
                                </Badge>
                              )}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <button
                          onClick={() => navigate(item.path)}
                          className={cn(
                            "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 relative",
                            active
                              ? "bg-emerald-500/20 text-emerald-500"
                              : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground",
                          )}
                        >
                          <item.icon
                            className={cn(
                              "h-5 w-5 shrink-0",
                              active ? "text-emerald-500" : "",
                            )}
                          />
                          <span className="font-medium text-sm flex-1 text-left">
                            {item.title}
                          </span>
                          {item.badge && (
                            <Badge className="h-5 min-w-5 px-1.5 text-[10px] bg-emerald-500/20 text-emerald-500 border-0">
                              {item.badge}
                            </Badge>
                          )}
                          {active && (
                            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-emerald-500 rounded-r-full" />
                          )}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </nav>
          </div>

          {/* Sidebar Footer */}
          <div className="border-t border-sidebar-border p-3 shrink-0">
            {!isCollapsed && (
              <div className="rounded-lg bg-sidebar-accent/50 p-3 mb-3 space-y-1">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-emerald-500" />
                  <span className="text-xs font-medium">Verified Vendor</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                  <span className="text-xs text-sidebar-foreground/70">
                    Active Status
                  </span>
                </div>
              </div>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-sidebar-accent transition-colors">
                  <Avatar className="h-8 w-8 border-2 border-emerald-500/30 shrink-0">
                    <AvatarImage src={profile?.avatar} />
                    <AvatarFallback className="bg-emerald-500/20 text-emerald-500 text-xs">
                      {profile?.firstName?.charAt(0).toUpperCase() || "V"}
                    </AvatarFallback>
                  </Avatar>
                  {/* {!isCollapsed && (
                    <>
                      <div className="flex flex-col items-start flex-1 min-w-0">
                        <span className="text-sm font-medium truncate">
                          {profile?.exchangers[0]?.name || "Vendor"}
                        </span>
                        <span className="text-xs text-sidebar-foreground/60 truncate">
                          {profile?.exchangers[0]?.email || "vendor@buyfiat.app"}
                        </span>
                      </div>
                      <ChevronDown className="h-4 w-4 text-sidebar-foreground/50 shrink-0" />
                    </>
                  )} */}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/vendor/settings")}>
                  <User className="mr-2 h-4 w-4" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/vendor/settings")}>
                  <Settings className="mr-2 h-4 w-4" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-red-500"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </aside>

        {/* Main Content Area */}
        <div
          className={cn(
            "flex-1 flex flex-col min-w-0 transition-all duration-300 ease-in-out",
            "lg:ml-0",
            isCollapsed ? "lg:ml-[70px]" : "lg:ml-[260px]",
          )}
        >
          {/* Mobile Header - Clean with back button */}
          <header className="lg:hidden fixed top-0 left-0 right-0 z-30 h-14 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b flex items-center justify-between px-4">
            {/* Left: Back button on sub-pages */}
            <div className="flex items-center gap-3">
              {isSubPage ? (
                <button
                  onClick={() => navigate(-1)}
                  className="flex items-center justify-center w-9 h-9 rounded-full hover:bg-muted transition-colors"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
              ) : (
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600">
                  <Store className="h-4 w-4 text-white" />
                </div>
              )}
              <span className="font-semibold text-base">{getPageTitle()}</span>
            </div>

            {/* Right: New Listing + Notifications */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate("/vendor/listing/new")}
                className="flex items-center justify-center w-8 h-8 rounded-full bg-emerald-500 hover:bg-emerald-600 transition-colors"
              >
                <PlusCircle className="h-4 w-4 text-white" />
              </button>
              <button
                onClick={() => navigate("/vendor/messages")}
                className="relative flex items-center justify-center w-9 h-9 rounded-full hover:bg-muted transition-colors"
              >
                <Bell className="h-5 w-5" />
                <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-emerald-500" />
              </button>
            </div>
          </header>

          {/* Desktop Header */}
          <header
            className={cn(
              "hidden lg:flex fixed top-0 left-0 right-0 z-30 h-16 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 items-center justify-between gap-4 px-6 transition-all duration-300 ease-in-out",
              isCollapsed ? "lg:left-[70px]" : "lg:left-[260px]",
            )}
          >
            {/* Left Section */}
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <button
                className="flex p-2 hover:bg-muted rounded-lg shrink-0"
                onClick={() => setIsCollapsed(!isCollapsed)}
              >
                {isCollapsed ? (
                  <ChevronRight className="h-5 w-5" />
                ) : (
                  <ChevronLeft className="h-5 w-5" />
                )}
              </button>

              {/* Breadcrumb */}
              <nav className="flex items-center gap-2 text-sm text-muted-foreground min-w-0">
                <span
                  className="hover:text-foreground cursor-pointer truncate"
                  onClick={() => navigate("/forex")}
                >
                  Forex
                </span>
                <span>/</span>
                <span
                  className="hover:text-foreground cursor-pointer truncate"
                  onClick={() => navigate("/vendor")}
                >
                  Vendor
                </span>
              </nav>
            </div>

            {/* Right Section */}
            <div className="flex items-center gap-2 shrink-0">
              <Button
                size="sm"
                className="bg-emerald-500 hover:bg-emerald-600"
                onClick={() => navigate("/vendor/listing/new")}
              >
                <PlusCircle className="mr-2 h-4 w-4" />
                New Listing
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative">
                    <Bell className="h-5 w-5" />
                    <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-emerald-500 text-[10px] font-medium text-white flex items-center justify-center">
                      3
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80">
                  <DropdownMenuLabel className="flex items-center justify-between">
                    Notifications
                    <Badge variant="secondary" className="text-[10px]">
                      3 new
                    </Badge>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {[
                    {
                      title: "New message",
                      desc: "From John D.",
                      time: "5 min ago",
                    },
                    {
                      title: "Trade completed",
                      desc: "Trade #FX-1234",
                      time: "1 hour ago",
                    },
                    {
                      title: "New review",
                      desc: "5-star review received",
                      time: "2 hours ago",
                    },
                  ].map((notif, i) => (
                    <DropdownMenuItem
                      key={i}
                      className="flex flex-col items-start gap-1 p-3 cursor-pointer"
                    >
                      <span className="font-medium text-sm">{notif.title}</span>
                      <span className="text-xs text-muted-foreground">
                        {notif.desc} • {notif.time}
                      </span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>

          {/* Page Content - with padding for headers */}
          <main className="flex-1 p-4 lg:p-6 overflow-auto pt-16 lg:pt-20 pb-24 lg:pb-6">
            <div className="mx-auto max-w-7xl">
              <Outlet />
            </div>
          </main>
        </div>

        {/* Mobile Bottom Navigation */}
        <div className="lg:hidden">
          <MobileBottomNav userType="vendor" />
        </div>
      </div>
    </TooltipProvider>
  );
}
